/**
 * Contador de expiração em segundos, com a regra de acessibilidade do OTP
 * (ux.md §6.1): o número visual NÃO é uma região `aria-live` contínua — isso
 * interromperia o leitor de tela 60x por minuto. Em vez disso, dispara um
 * único callback (`onCrossUnder1Min`) na transição para menos de 1 minuto
 * restante, e nunca repete.
 *
 * Usado nos dois cronômetros de expiração do OTP (código e escolha de
 * empresa) — o cooldown de reenvio usa a mesma classe só pelo `onTick`,
 * sem precisar do aviso de 1 minuto (nunca passa de 60s).
 */
export class Countdown {
  secondsLeft = 0;

  onTick?: (secondsLeft: number) => void;
  onExpire?: () => void;
  onCrossUnder1Min?: () => void;

  private intervalId: ReturnType<typeof setInterval> | undefined;
  private announcedUnder1Min = false;

  start(totalSeconds: number): void {
    this.stop();
    this.secondsLeft = Math.max(0, Math.floor(totalSeconds));
    // Se já nasce com menos de 1 min, não há "cruzamento" a anunciar —
    // a pessoa já vê o número baixo desde o início.
    this.announcedUnder1Min = this.secondsLeft < 60;

    if (this.secondsLeft <= 0) {
      this.onExpire?.();
      return;
    }

    this.intervalId = setInterval(() => {
      this.secondsLeft -= 1;

      if (!this.announcedUnder1Min && this.secondsLeft < 60) {
        this.announcedUnder1Min = true;
        this.onCrossUnder1Min?.();
      }

      if (this.secondsLeft <= 0) {
        this.stop();
        this.onExpire?.();
        return;
      }

      this.onTick?.(this.secondsLeft);
    }, 1000);
  }

  stop(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }
}

/** Formata segundos como `mm:ss` para os cronômetros de expiração. */
export function formatMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}
